import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { dateTimeUtils, DEFAULT_TIMEZONE, SUPPORTED_TIMEZONES } from '../utils/dateTimeUtils';

/**
 * Custom hook for timezone-aware date/time operations
 * Uses the school's timezone from auth context
 */
export const useTimezone = () => {
  const { timezone } = useAuth();

  return useMemo(() => ({
    /** Current school timezone */
    timezone,
    
    /** Default timezone (PKT) */
    defaultTimezone: DEFAULT_TIMEZONE,
    
    /** All supported timezones */
    supportedTimezones: SUPPORTED_TIMEZONES,
    
    /** Get today's date string (YYYY-MM-DD) in school timezone */
    getToday: () => dateTimeUtils.getTodayInTimezone(timezone),
    
    /** Get current time in school timezone */
    getCurrentTime: () => dateTimeUtils.getCurrentTimeInTimezone(timezone),
    
    /** Format a UTC date/time for display */
    formatDateTime: (date: Date | string, formatStr?: string) => 
      dateTimeUtils.formatDateTimeForDisplay(date, timezone, formatStr),
    
    /** Format a UTC date for display (date only) */
    formatDate: (date: Date | string, formatStr?: string) => 
      dateTimeUtils.formatDateForDisplay(date, timezone, formatStr),
    
    /** Format time only */
    formatTime: (date: Date | string, formatStr?: string) => 
      dateTimeUtils.formatTimeForDisplay(date, timezone, formatStr),
    
    /** Get date string from a UTC timestamp */
    getDateFromTimestamp: (date: Date | string) => 
      dateTimeUtils.getDateFromTimestamp(date, timezone),
    
    /** Check if a date is today */
    isToday: (dateStr: string) => dateTimeUtils.isToday(dateStr, timezone),
    
    /** Check if a date is in the future */
    isFutureDate: (dateStr: string) => dateTimeUtils.isFutureDate(dateStr, timezone),
    
    /** Get relative time description */
    getRelativeTime: (date: Date | string) => dateTimeUtils.getRelativeTime(date, timezone),
    
    /** Format duration in minutes */
    formatDuration: dateTimeUtils.formatDuration,
    
    /** Get timezone display label */
    getTimezoneLabel: () => dateTimeUtils.getTimezoneLabel(timezone),
  }), [timezone]);
};

export default useTimezone;
